import { apiClient } from "../../../services/apiClient";

export type TriageProcessFlowOption = Readonly<{
  id: string;
  name: string;
  definitionId: string;
}>;

type ProcessTemplateInfo = {
  id: number;
  name: string;
  definition_id?: string;
  description?: string;
};

type ProcessTemplateGroup = {
  process_template: ProcessTemplateInfo;
  sub_process_templates?: ProcessTemplateInfo[];
};

function normalizeTemplateInfo(raw: unknown): ProcessTemplateInfo | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const id = Number(record.id);
  if (!Number.isFinite(id)) return null;
  return {
    id,
    name: String(record.name ?? "").trim(),
    definition_id:
      typeof record.definition_id === "string" ? record.definition_id : "",
    description:
      typeof record.description === "string" ? record.description : "",
  };
}

function normalizeProcessTemplateGroups(
  data: unknown,
): ProcessTemplateGroup[] {
  if (!Array.isArray(data)) return [];
  const out: ProcessTemplateGroup[] = [];
  for (const row of data) {
    if (!row || typeof row !== "object") continue;
    const record = row as Record<string, unknown>;
    const process_template = normalizeTemplateInfo(record.process_template);
    if (!process_template) continue;
    const subsRaw = record.sub_process_templates;
    const sub_process_templates = Array.isArray(subsRaw)
      ? subsRaw
          .map((item) => normalizeTemplateInfo(item))
          .filter((item): item is ProcessTemplateInfo => item !== null)
      : [];
    out.push({ process_template, sub_process_templates });
  }
  return out;
}

/** Flatten main + sub process templates into selectable Flow options. */
export function flattenProcessFlowOptions(
  groups: readonly ProcessTemplateGroup[],
): TriageProcessFlowOption[] {
  const out: TriageProcessFlowOption[] = [];
  const seen = new Set<string>();
  for (const group of groups) {
    const main = group.process_template;
    const mainId = String(main.id);
    if (!seen.has(mainId)) {
      seen.add(mainId);
      out.push({
        id: mainId,
        name: main.name.trim() || mainId,
        definitionId: main.definition_id ?? "",
      });
    }
    for (const sub of group.sub_process_templates ?? []) {
      const subId = String(sub.id);
      if (seen.has(subId)) continue;
      seen.add(subId);
      out.push({
        id: subId,
        name: sub.name.trim() || subId,
        definitionId: sub.definition_id ?? "",
      });
    }
  }
  return out;
}

/**
 * Load process flows for Triage Plan filter / Then-step Flow select.
 * GET /manual-assessment/process-templates
 */
export async function fetchTriageProcessFlows(): Promise<
  TriageProcessFlowOption[]
> {
  const result = await apiClient.get<unknown>(
    "/manual-assessment/process-templates",
  );
  return flattenProcessFlowOptions(
    normalizeProcessTemplateGroups(result.data),
  );
}
